import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { PlanType } from 'webapp/enums/enums';

import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

import type BillingPlan from 'webapp/models-custom/billing-plan';

import type { TrackingOriginForSubscriptionChangePopup } from 'webapp/services/billing';

export default abstract class BillingPopupPlanUpgradeBaseComponent<
	TData extends object = PopupData.Any,
> extends PopupInnerModalComponent<TData> {
	@service('billing') declare billingService: Service.Billing;

	abstract get targetPlan(): BillingPlan | null;
	abstract get planPrice(): string | null;
	abstract get pricingTitle(): string | null;
	abstract get onPlanUpgradeFn(): ((hasUpgraded: boolean) => void) | undefined;

	get planType() {
		return this.targetPlan?.planType;
	}

	get planName() {
		return this.targetPlan?.name;
	}

	get isCurrentCycleMonthly() {
		const plan = this.billingService.activePlan;
		return plan?.isFree || plan?.isMonthly;
	}

	get planHightlightText(): string {
		const planType = this.targetPlan?.planType;

		if (!planType || this.targetPlan.is2026SeptPlan) {
			return '';
		}
		switch (planType) {
			case PlanType.STARTER:
				return 'Client reporting, automated from day one.';
			case PlanType.ANALYST:
				return 'Unlimited dashboards & reports<br>More sources. Datasets. More historical data.<br>10x more AI credits.';
			case PlanType.PROFESSIONAL:
			case PlanType.PERFORMER:
				return 'Unlimited users. Effortless sharing.<br>Reporting on autopilot.';
			case PlanType.GROWTH:
				return 'OKRs. Real human support.';
			case PlanType.PREMIUM:
				return 'Built around how your organization actually works.';
			case PlanType.CUSTOM:
			case PlanType.FREE:
			case PlanType.VIP:
			case PlanType.AGENCY:
			case PlanType.TEAM_CORE:
			case PlanType.TEAM_SCALE:
				return '';
		}
	}

	get planFeatures(): string[] {
		const plan = this.targetPlan;
		if (!plan) {
			return [];
		}

		const features: string[] = [];

		if (this.targetPlan?.isCustom) {
			features.push(...this.billingService.customPlanFeatureItems);
		} else {
			// The top (highest-tier) enabled feature items, read from the end of the list.
			const higherTierFeatures = this.billingService
				.getEnabledPlanFeatureItems(plan)
				.items.slice(-4)
				.map((item) => item.name);

			features.push(...higherTierFeatures);
		}

		return features;
	}

	get imageSrc() {
		let planType = this.targetPlan?.planType;
		if (!planType || this.billingService.is2026SeptBillingGroup) {
			// fallback for missing image or new plans
			planType = PlanType.GROWTH;
		}

		return `/images/billing/forbidden-feature/${planType}.png`;
	}

	@action
	openSubscriptionPopup(trackingOrigin?: TrackingOriginForSubscriptionChangePopup | null) {
		const targetPlan = this.targetPlan;
		if (!targetPlan) {
			return;
		}

		this.billingService.switchPlan(
			targetPlan,
			{
				billingPeriodChangePopup: trackingOrigin || 'limitation_popup',
			},
			undefined,
			() => this.onPlanUpgradeFn?.(true),
		);
	}
}
